import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Gift, 
  Droplets, 
  Clock, 
  Server, 
  Settings, 
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Info
} from 'lucide-react';

const toolCategories = {
  token: [
    {
      id: 'distribution',
      title: 'Distribution',
      icon: Gift,
      tools: [
        { 
          type: 'preallocation', 
          name: 'Preallocation', 
          description: 'Allocate tokens to identities at launch',
          icon: Gift,
          color: 'emerald'
        },
      ],
    },
  ],
  basket: [
    {
      id: 'distribution',
      title: 'Distribution',
      icon: Gift,
      tools: [
        { 
          type: 'preallocation', 
          name: 'Preallocation', 
          description: 'Allocate tokens to identities at launch',
          icon: Gift,
          color: 'emerald'
        },
      ],
    },
    {
      id: 'reserves',
      title: 'Reserve Currencies',
      icon: Droplets,
      tools: [
        { 
          type: 'reserveCurrency', 
          name: 'Reserve Currency', 
          description: 'Add a backing currency to the basket',
          icon: Droplets,
          color: 'amber'
        },
      ],
    },
  ],
  pbaas: [
    {
      id: 'distribution',
      title: 'Distribution',
      icon: Gift,
      tools: [
        { 
          type: 'preallocation', 
          name: 'Preallocation', 
          description: 'Allocate tokens to identities at launch',
          icon: Gift,
          color: 'emerald'
        },
      ],
    },
    {
      id: 'reserves',
      title: 'Reserve Currencies',
      icon: Droplets,
      tools: [
        { 
          type: 'reserveCurrency', 
          name: 'Reserve Currency', 
          description: 'Add a backing currency',
          icon: Droplets,
          color: 'amber'
        },
      ],
    },
    {
      id: 'blockchain',
      title: 'Blockchain Settings',
      icon: Clock,
      tools: [
        { 
          type: 'era', 
          name: 'Era Configuration', 
          description: 'Define block rewards and halving',
          icon: Clock,
          color: 'violet'
        },
        { 
          type: 'nodeSettings', 
          name: 'Bootstrap Node', 
          description: 'Add a network bootstrap node',
          icon: Server,
          color: 'pink'
        },
        { 
          type: 'gatewayConverter', 
          name: 'Gateway Converter', 
          description: 'Configure the Bridge liquidity pool',
          icon: Droplets,
          color: 'orange'
        },
        { 
          type: 'advancedOptions', 
          name: 'Advanced Options', 
          description: 'Configure chain parameters',
          icon: Settings,
          color: 'cyan'
        },
      ],
    },
  ],
  erc20: [
    {
      id: 'mapping',
      title: 'Token Mapping',
      icon: ArrowRightLeft,
      tools: [
        { 
          type: 'erc20', 
          name: 'ERC-20 Contract', 
          description: 'Map an Ethereum token address',
          icon: ArrowRightLeft,
          color: 'orange'
        },
      ],
    },
  ],
};

const colorClasses = {
  emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20',
  amber: 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20',
  violet: 'bg-violet-500/10 border-violet-500/30 text-violet-400 hover:bg-violet-500/20',
  pink: 'bg-pink-500/10 border-pink-500/30 text-pink-400 hover:bg-pink-500/20',
  cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20',
  orange: 'bg-orange-500/10 border-orange-500/30 text-orange-400 hover:bg-orange-500/20',
  teal: 'bg-teal-500/10 border-teal-500/30 text-teal-400 hover:bg-teal-500/20',
};

const Sidebar = ({ currencyType, onAddNode }) => {
  const [expandedCategories, setExpandedCategories] = useState(
    Object.fromEntries(
      (toolCategories[currencyType.id] || []).map(cat => [cat.id, true])
    )
  );
  const [, setDraggedTool] = useState(null);

  const categories = toolCategories[currencyType.id] || [];

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleDragStart = (e, tool) => {
    setDraggedTool(tool);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragEnd = () => {
    setDraggedTool(null);
  };

  return (
    <div className="w-72 bg-slate-800 border-r border-slate-700/50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-slate-700/50">
        <h3 className="text-sm font-semibold text-white mb-1">Toolbox</h3>
        <p className="text-xs text-slate-400">Drag components to the canvas</p>
      </div>

      {/* Tool Categories */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {categories.map((category) => (
          <div key={category.id} className="bg-slate-900 rounded-xl overflow-hidden border border-slate-700/50">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(category.id)}
              className="w-full flex items-center gap-3 p-3 bg-slate-800 hover:bg-slate-700 transition-colors"
            >
              <category.icon className="w-4 h-4 text-slate-300" />
              <span className="flex-1 text-left text-sm font-medium text-white">
                {category.title}
              </span>
              {expandedCategories[category.id] ? (
                <ChevronDown className="w-4 h-4 text-slate-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-500" />
              )}
            </button>

            {/* Tools */}
            <AnimatePresence>
              {expandedCategories[category.id] && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="px-2 pb-2 space-y-1"
                >
                  {category.tools.map((tool) => (
                    <motion.div
                      key={tool.type}
                      draggable
                      onDragStart={(e) => handleDragStart(e, tool)}
                      onDragEnd={handleDragEnd}
                      onClick={() => onAddNode(tool.type, { x: 100, y: 150 })}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`
                        flex items-center gap-3 p-3 rounded-lg border cursor-grab active:cursor-grabbing
                        ${colorClasses[tool.color]}
                        transition-colors duration-200
                      `}
                    >
                      <GripVertical className="w-4 h-4 opacity-50" />
                      <tool.icon className="w-5 h-5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{tool.name}</p>
                        <p className="text-xs opacity-70 truncate">{tool.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}

        {/* Info Panel */}
        <div className="mt-4 p-3 bg-indigo-500/10 rounded-xl border border-indigo-500/20">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-indigo-400 mt-0.5" />
            <div>
              <p className="text-xs text-indigo-300 font-medium mb-1">Quick Tips</p>
              <ul className="text-xs text-indigo-400/80 space-y-1">
                <li>• Click or drag tools to add nodes</li>
                <li>• Connect nodes by dragging handles</li>
                <li>• All changes update in real-time</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-slate-700/50">
        <div className="text-xs text-slate-500 text-center">
          {currencyType.title} • options: {currencyType.options}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
